import { Command } from 'commander';
import { log, die } from '../utils.js';
import { configExists, createApiClient } from '../config.js';
import { printLegacyNotice } from '../legacy-notice.js';
import { notSignedInMessage } from '../active-org.js';
import {
  addOutputOption,
  resolveOutputFormat,
  serializeStructured,
  structuredErrorPayload,
} from '../services/output-format.js';

/**
 * Build the `hooks` command tree. A factory (not a singleton) so the same
 * verb can mount under both the top-level program (deprecated, BL598 Phase 1A)
 * and the `admin` parent (canonical) without a commander parent collision.
 */
export function makeHooksCommand(): Command {
const hooksCommand = new Command('hooks').description('Manage webhook hooks');

/**
 * grid hooks add <trigger> <slack-url> [--app my-app]
 */
hooksCommand
  .command('add <trigger> <url>')
  .description('Add a Slack webhook hook for a trigger event')
  .option('--app <name>', 'Scope hook to a specific app (omit for platform-level)')
  .action(async (trigger, url, opts) => {
    printLegacyNotice();
    if (!configExists()) die(notSignedInMessage());
    try {
      const result = await createApiClient().addHook({ trigger, url, app: opts.app || null });
      log.success(`Hook created: ${result.hook.id} (${trigger}${opts.app ? ` → ${opts.app}` : ' → platform'})`);
    } catch (err: any) {
      die(`Failed: ${err.message}`);
    }
  });

/**
 * grid hooks list [--app my-app] [-o json|yaml|table|wide]
 *
 * BL621 Phase 3 — `hooks list` is a read verb, so it honors the shared
 * `-o` / `--output` flag. In structured (json|yaml) mode the hook array is
 * emitted to stdout and any failure (including not-signed-in) is emitted as
 * the `{ error: { code, message } }` envelope on stdout with a non-zero exit.
 */
const hooksListCommand = addOutputOption(
  hooksCommand
    .command('list')
    .description('List configured hooks')
    .option('--app <name>', 'Filter by app')
    .option('--json', 'machine-readable output (alias of -o json)'),
);
hooksListCommand.action(async (opts) => {
  const fmt = resolveOutputFormat(opts);
  const structured = fmt === 'json' || fmt === 'yaml';
  const fail = (code: string, message: string): never => {
    if (structured) {
      process.stdout.write(serializeStructured(fmt, structuredErrorPayload({ code, message })));
      process.exit(1);
    }
    die(message);
  };
  if (!structured) printLegacyNotice();
  if (!configExists()) fail('NOT_SIGNED_IN', notSignedInMessage());
  try {
    const result = await createApiClient().listHooks(opts.app);
    if (structured) {
      process.stdout.write(serializeStructured(fmt, result.hooks));
      return;
    }
    if (result.hooks.length === 0) {
      log.info('No hooks configured.');
      return;
    }
    console.log('\n  HOOKS');
    console.log('  ' + '-'.repeat(60));
    for (const h of result.hooks) {
      const scope = h.app ? `app: ${h.app}` : 'platform';
      console.log(`  ${h.id}  ${h.trigger.padEnd(25)} ${scope}`);
    }
    console.log('');
  } catch (err: any) {
    fail('HOOKS_LIST_FAILED', `Failed: ${err.message}`);
  }
});

/**
 * grid hooks remove <id>
 */
hooksCommand
  .command('remove <id>')
  .description('Remove a hook by ID')
  .action(async (id) => {
    printLegacyNotice();
    if (!configExists()) die(notSignedInMessage());
    try {
      await createApiClient().removeHook(id);
      log.success(`Hook removed: ${id}`);
    } catch (err: any) {
      die(`Failed: ${err.message}`);
    }
  });

/**
 * grid hooks deliveries [--limit N]
 */
hooksCommand
  .command('deliveries')
  .description('Show recent hook delivery log')
  .option('--limit <n>', 'Number of deliveries to show', '20')
  .action(async (opts) => {
    printLegacyNotice();
    if (!configExists()) die(notSignedInMessage());
    try {
      const result = await createApiClient().listDeliveries(parseInt(opts.limit, 10));
      if (result.deliveries.length === 0) {
        log.info('No hook deliveries recorded.');
        return;
      }
      console.log('\n  DELIVERIES');
      console.log('  ' + '-'.repeat(70));
      for (const d of result.deliveries) {
        const status = d.status === 'success' ? '✓' : '✗';
        const code = d.response_code ? ` (${d.response_code})` : '';
        const ts = d.timestamp.slice(0, 19).replace('T', ' ');
        console.log(`  ${status} ${ts}  ${d.trigger.padEnd(25)} ${d.app || 'platform'}${code}`);
      }
      console.log('');
    } catch (err: any) {
      die(`Failed: ${err.message}`);
    }
  });

  return hooksCommand;
}

export const hooksCommand = makeHooksCommand();
